import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { CallOutcome, CallStatus, CampaignStatus, LeadStatus } from '@fenix/shared';

export const fmtDateTime = (iso: string | null | undefined) =>
  iso ? format(new Date(iso), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : '—';
export const fmtDateTimeSec = (iso: string | null | undefined) =>
  iso ? format(new Date(iso), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR }) : '—';
export const fmtDate = (iso: string | null | undefined) =>
  iso ? format(new Date(iso), 'dd/MM/yyyy', { locale: ptBR }) : '—';
export const fmtRelative = (iso: string | null | undefined) =>
  iso ? formatDistanceToNow(new Date(iso), { addSuffix: true, locale: ptBR }) : '—';
export const fmtDuration = (sec: number | null | undefined) => {
  if (sec == null) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m ? `${m}m ${s}s` : `${s}s`;
};
export const fmtUsd = (v: number | null | undefined) => (v == null ? '—' : `US$ ${v.toFixed(2)}`);
export const fmtPct = (v: number) => `${(v * 100).toFixed(0)}%`;
export const fmtPhone = (e164: string) => {
  if (e164.startsWith('+55') && e164.length === 14)
    return `+55 (${e164.slice(3, 5)}) ${e164.slice(5, 10)}-${e164.slice(10)}`;
  if (e164.startsWith('+1') && e164.length === 12)
    return `+1 (${e164.slice(2, 5)}) ${e164.slice(5, 8)}-${e164.slice(8)}`;
  return e164;
};

export const LEAD_STATUS_LABEL: Record<LeadStatus, string> = {
  new: 'Novo',
  queued: 'Na fila',
  calling: 'Em ligação',
  contacted: 'Contatado',
  recovered: 'Recuperado',
  not_interested: 'Sem interesse',
  failed: 'Falhou',
  do_not_call: 'Não ligar',
};
export const LEAD_STATUS_VARIANT: Record<LeadStatus, string> = {
  new: 'bg-info/15 text-info',
  queued: 'bg-muted text-muted-foreground',
  calling: 'bg-warning/20 text-amber-700 dark:text-amber-300 animate-pulse',
  contacted: 'bg-secondary text-secondary-foreground',
  recovered: 'bg-success/15 text-green-700 dark:text-green-300',
  not_interested: 'bg-muted text-muted-foreground',
  failed: 'bg-destructive/10 text-destructive',
  do_not_call: 'bg-destructive/15 text-destructive',
};

export const CALL_STATUS_LABEL: Record<CallStatus, string> = {
  queued: 'Na fila',
  ringing: 'Chamando',
  in_progress: 'Em andamento',
  ended: 'Concluída',
  failed: 'Falhou',
  no_answer: 'Não atendeu',
  busy: 'Ocupado',
  voicemail: 'Caixa postal',
};
export const CALL_STATUS_VARIANT: Record<CallStatus, string> = {
  queued: 'bg-muted text-muted-foreground',
  ringing: 'bg-warning/20 text-amber-700 dark:text-amber-300 animate-pulse',
  in_progress: 'bg-info/15 text-info animate-pulse',
  ended: 'bg-success/15 text-green-700 dark:text-green-300',
  failed: 'bg-destructive/10 text-destructive',
  no_answer: 'bg-muted text-muted-foreground',
  busy: 'bg-muted text-muted-foreground',
  voicemail: 'bg-muted text-muted-foreground',
};

/** Para selects: em ordem alfabética do que o usuário lê, com acentos e caixa tratados. */
export const sortByName = <T extends { name: string }>(items: readonly T[]) =>
  [...items].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }));

/** Motivos de "pulado" numa campanha que não são resultado de chamada. */
export const SKIP_REASON_LABEL: Record<string, string> = {
  product_inactive: 'Produto desativado',
  product_no_offer: 'Produto sem oferta',
  product_no_link: 'Produto sem link de checkout',
  product_no_coupon: 'Produto sem cupom',
};

/** Status do e-mail (segunda via do link), conforme os webhooks do Resend. */
export const EMAIL_STATUS_LABEL: Record<string, string> = {
  queued: 'Na fila',
  sent: 'Enviado',
  delayed: 'Atrasado',
  delivered: 'Entregue',
  opened: 'Aberto',
  clicked: 'Link clicado',
  bounced: 'Devolvido',
  complained: 'Marcado como spam',
  failed: 'Falhou',
};
export const EMAIL_STATUS_VARIANT: Record<string, string> = {
  delivered: 'bg-success/15 text-success',
  opened: 'bg-success/15 text-success',
  clicked: 'bg-success/15 text-success',
  bounced: 'bg-destructive/10 text-destructive',
  complained: 'bg-destructive/10 text-destructive',
  failed: 'bg-destructive/10 text-destructive',
  delayed: 'bg-warning/20 text-warning',
};
/** Por que o e-mail saiu. */
export const EMAIL_REASON_LABEL: Record<string, string> = {
  sms_failed: 'SMS não entregue',
  ai_requested: 'Combinado na ligação',
  manual: 'Enviado pelo painel',
};

export const OUTCOME_LABEL: Record<CallOutcome, string> = {
  recovered: 'Venda recuperada',
  callback: 'Pediu retorno',
  not_interested: 'Sem interesse',
  no_answer: 'Não atendeu',
  voicemail: 'Caixa postal',
  do_not_call: 'Pediu para não ligar',
  wrong_number: 'Número errado',
  other: 'Outro',
};

export const CAMPAIGN_STATUS_LABEL: Record<CampaignStatus, string> = {
  draft: 'Rascunho',
  running: 'Em execução',
  paused: 'Pausada',
  done: 'Concluída',
};
export const CAMPAIGN_STATUS_VARIANT: Record<CampaignStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  running: 'bg-success/15 text-green-700 dark:text-green-300 animate-pulse',
  paused: 'bg-warning/20 text-amber-700 dark:text-amber-300',
  done: 'bg-secondary text-secondary-foreground',
};
